<script lang="ts">
  /**
   * A daily series as a line, with an area fill, a faint grid and an emphasised
   * endpoint. The support act for a verdict, never the headline.
   *
   * ## Why this is inline SVG when the estate bans inline SVG
   *
   * `07-acceptance.md` rule 9 is about *glyphs*: "All glyphs from Icon.svelte. If
   * the glyph does not exist, add it to the map — do not draw it locally." That
   * rule exists because hand-drawn glyphs flattened multi-element icons into
   * featureless boxes. A chart is geometry computed from data, not a glyph, and
   * there is no icon that can express ninety days of run-hours. Every actual
   * glyph on this screen still comes from `Icon`.
   *
   * ## Gaps are gaps
   *
   * The line breaks where a day has no reading rather than joining across it. A
   * continuous line over a missing day is an interpolation, and being honest
   * about missing data is the entire reason this module exists — the same
   * argument the History screen makes for using bars.
   *
   * ## Geometry
   *
   * `preserveAspectRatio="none"` lets the viewBox stretch to whatever width the
   * container gives, which is what makes the chart responsive without measuring
   * anything in JS. It distorts strokes, so every stroke carries
   * `vector-effect="non-scaling-stroke"`. It also distorts circles, which is why
   * the endpoint marker is an HTML element positioned in percentages over the
   * SVG rather than an SVG circle.
   */
  import { fig, type Point } from "../answers";

  let {
    points,
    tone = "ok",
    unit = "",
    /** Shared across a pair of charts so both read against one axis. */
    max = null,
    /** A server-computed mean drawn as a dashed rule. Never re-derived here. */
    reference = null,
    height = 150,
    label = "",
  }: {
    points: Point[];
    tone?: "ok" | "watch" | "thin";
    unit?: string;
    max?: number | null;
    reference?: { value: number; label: string } | null;
    height?: number;
    label?: string;
  } = $props();

  /** Vertical padding inside the viewBox, so the stroke and marker are not clipped. */
  const TOP = 4;
  const BOTTOM = 96;

  const values = $derived(points.map((p) => p.value));
  const peak = $derived(max ?? Math.max(...values.map((v) => v ?? 0), 0));
  /** A flat zero series would divide by zero; 1 keeps the baseline on the floor. */
  const scale = $derived(peak > 0 ? peak : 1);
  const span = $derived(Math.max(points.length - 1, 1));

  const x = (i: number) => (i / span) * 100;
  const y = (v: number) => BOTTOM - (Math.min(v, scale) / scale) * (BOTTOM - TOP);

  /** Contiguous runs of days that actually reported. One path each. */
  const runs = $derived.by(() => {
    const out: { i: number; v: number }[][] = [];
    let cur: { i: number; v: number }[] = [];
    points.forEach((p, i) => {
      if (p.value === null) {
        if (cur.length > 0) out.push(cur);
        cur = [];
      } else {
        cur.push({ i, v: p.value });
      }
    });
    if (cur.length > 0) out.push(cur);
    return out;
  });

  const line = (run: { i: number; v: number }[]) =>
    run.map((p, n) => `${n === 0 ? "M" : "L"}${x(p.i).toFixed(2)},${y(p.v).toFixed(2)}`).join(" ");

  /** The same run, closed to the baseline. Decorative — no text sits on it. */
  const area = (run: { i: number; v: number }[]) =>
    run.length < 2
      ? ""
      : `${line(run)} L${x(run[run.length - 1]!.i).toFixed(2)},${BOTTOM} L${x(run[0]!.i).toFixed(2)},${BOTTOM} Z`;

  const last = $derived(runs[runs.length - 1]?.at(-1) ?? null);
  const gaps = $derived(values.filter((v) => v === null).length);
  /** Five pixels a day keeps ninety days legible; the container scrolls past that. */
  const minWidth = $derived(Math.max(280, points.length * 5));
</script>

<figure class="t" data-tone={tone} aria-label={label || `${points.length} days, peak ${peak}${unit}`}>
  {#if points.length === 0}
    <!-- An em dash, never a flat line at zero. A chart drawn along the baseline
         reads as "the pump never ran", which is a different claim from "no day
         in this window has been rolled up yet". -->
    <p class="t__none">— no rolled-up days in this window.</p>
  {:else}
  <!-- Its own scroller. The page body never scrolls sideways; a wide chart does. -->
  <div class="t__scroll">
    <div class="t__plot" style="height:{height}px; min-width:{minWidth}px">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <!-- Faint grid: floor, midpoint, ceiling. Three lines is enough to read a
             height against and few enough to stay behind the data. -->
        {#each [TOP, (TOP + BOTTOM) / 2, BOTTOM] as gy (gy)}
          <line x1="0" x2="100" y1={gy} y2={gy} class="t__grid" vector-effect="non-scaling-stroke" />
        {/each}

        {#if reference && peak > 0}
          <line
            x1="0"
            x2="100"
            y1={y(reference.value)}
            y2={y(reference.value)}
            class="t__ref"
            vector-effect="non-scaling-stroke"
          />
        {/if}

        {#each runs as run, n (n)}
          {#if run.length > 1}
            <path d={area(run)} class="t__area" />
          {/if}
          <path d={line(run)} class="t__line" vector-effect="non-scaling-stroke" />
        {/each}
      </svg>

      <!-- The emphasised endpoint: where the series got to, which is what the
           verdict is about. HTML rather than an SVG circle because the stretched
           viewBox would render a circle as an ellipse. -->
      {#if last}
        <span class="t__end" style="left:{x(last.i)}%; top:{y(last.v)}%"></span>
      {/if}
    </div>
  </div>

  <!-- A legend, always. Operator surfaces do not get to assume the reader knows
       what the line is. -->
  <figcaption class="t__legend">
    <span class="t__key"><span class="t__swatch t__swatch--line"></span>{label || "per day"}</span>
    {#if reference}
      <span class="t__key"><span class="t__swatch t__swatch--ref"></span>{reference.label}</span>
    {/if}
    {#if gaps > 0}
      <span class="t__key">
        <span class="t__swatch t__swatch--gap"></span>{gaps} {gaps === 1 ? "day" : "days"} with no reading
      </span>
    {/if}
    <span class="t__key t__key--end">
      <span class="num">{points[0]?.day ?? "—"}</span>
      <span aria-hidden="true">→</span>
      <span class="num">{points[points.length - 1]?.day ?? "—"}</span>
      <span class="num">· peak {peak > 0 ? fig(peak) : "—"}{unit}</span>
    </span>
  </figcaption>
  {/if}
</figure>

<style>
  /* The tone is resolved once, into a variable rather than into `color`. The
     legend below is --mut, so `currentColor` would mean two different things in
     two parts of the same figure and the legend swatch would stop matching the
     line it describes. Blue for steady, amber for attention, grey for thin —
     and never green, whatever "improving" turns out to mean. */
  .t {
    margin: 0;
    --tone-c: var(--ok);
  }
  .t[data-tone="watch"] {
    --tone-c: var(--warn);
  }
  .t[data-tone="thin"] {
    --tone-c: var(--mut);
  }

  .t__none {
    margin: 0;
    padding: var(--sp-4) 0;
    font-size: var(--fs-small);
    color: var(--mut);
  }

  .t__scroll {
    overflow-x: auto;
  }
  .t__plot {
    position: relative;
    border-radius: var(--r-control);
    background: var(--fill);
    box-shadow: inset 0 0 0 1px var(--line);
    /* Horizontal padding, so the endpoint marker at x=100% has room to sit on
       the last point instead of being sliced in half by the scroller's edge. */
    padding: 0 7px;
  }
  svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .t__grid {
    stroke: var(--line);
    stroke-width: 1;
  }
  .t__ref {
    stroke: var(--mut);
    stroke-width: 1;
    stroke-dasharray: 3 4;
  }
  .t__line {
    fill: none;
    stroke: var(--tone-c);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  /* Decorative only — nothing is ever read against this fill, so it is not a
     contrast pair. It exists to give the line a body rather than a hairline.
     Kept low: at 18% it stopped being a fill under a line and became a filled
     block with an edge, which reads as a bar chart and buries the shape the
     verdict is talking about. */
  .t__area {
    fill: color-mix(in srgb, var(--tone-c) 10%, transparent);
    stroke: none;
  }

  .t__end {
    position: absolute;
    width: 9px;
    height: 9px;
    margin: -4.5px 0 0 -4.5px;
    border-radius: 50%;
    background: var(--tone-c);
    box-shadow: 0 0 0 3px var(--s1);
  }

  .t__legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--sp-1) var(--sp-3);
    padding-top: var(--sp-2);
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .t__key {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .t__key--end {
    margin-left: auto;
  }
  .t__swatch {
    width: 14px;
    height: 0;
    border-top: 2px solid currentColor;
    flex-shrink: 0;
  }
  .t__swatch--line {
    border-color: var(--tone-c);
  }
  .t__swatch--ref {
    border-top-style: dashed;
    border-color: var(--mut);
  }
  /* A gap is drawn as the absence of a line, so the legend swatch is an absence
     too — a break rather than a colour nobody could name. */
  .t__swatch--gap {
    border-top: 2px dotted var(--mut);
  }
</style>
